/* iter-53n++ pass 8 — Stripe Checkout return page for marketplace purchases.
 *
 * Flow:
 *   1. Stripe redirects buyer here with ?session_id=cs_test_...
 *   2. We poll /api/marketplace/checkout/status/{session_id} every 2s
 *      until status is "complete" or "failed" (Stripe webhook cadence
 *      can be a few seconds behind the redirect).
 *   3. On "complete" we route the buyer straight into their new project;
 *      on "failed" we show a friendly refund confirmation.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2, XCircle, ChevronLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { marketplaceApi } from '@/lib/api';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_MS      = 60000; // Stop after 60s; Stripe usually reconciles well under 10s.

export default function PaymentSuccessPage() {
  const [status, setStatus] = useState('checking');   // checking | complete | failed | timeout
  const [detail, setDetail] = useState('');
  const [spawnedId, setSpawnedId] = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setStatus('failed');
      setDetail('Missing session id — nothing to fulfill.');
      return () => {};
    }

    const started = Date.now();
    let timer = null;

    const poll = async () => {
      if (cancelledRef.current) return;
      try {
        const r = await marketplaceApi.checkoutStatus(sessionId);
        if (r.status === 'complete') {
          setSpawnedId(r.spawned_project_id || null);
          setStatus('complete');
          return;
        }
        if (r.status === 'failed') {
          setStatus('failed');
          setDetail(
            r.payment_status === 'refunded'
              ? 'Your card was refunded automatically — please try again.'
              : 'Payment did not complete.'
          );
          return;
        }
      } catch (e) {
        // Transient — keep polling until timeout.
      }
      if (Date.now() - started > MAX_POLL_MS) {
        setStatus('timeout');
        return;
      }
      timer = window.setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => { cancelledRef.current = true; if (timer) clearTimeout(timer); };
  }, []);

  const openWorkspace = () => window.location.assign('/app');
  const backToMarketplace = () => window.location.assign('/marketplace');

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg px-6" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-lg border border-panel-border bg-card p-8 shadow-panel">
        {status === 'checking' && (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="payment-checking">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <h1 className="text-[15px] font-semibold">Finalising your purchase…</h1>
            <p className="text-[12.5px] text-muted-foreground">
              Stripe is confirming your payment. This usually takes just a few seconds.</p>
          </div>
        )}

        {status === 'complete' && (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="payment-complete">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <h1 className="text-[15px] font-semibold">Purchase complete!</h1>
            <p className="text-[12.5px] text-muted-foreground">
              Your new project has been added to your workspace. Start writing whenever you&apos;re ready —
              85% of every sale goes to the author.
            </p>
            <Button onClick={openWorkspace} className="mt-2 w-full gap-2" data-testid="payment-open-workspace">
              <Sparkles className="h-4 w-4" /> Open my new project
            </Button>
          </div>
        )}

        {status === 'failed' && (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="payment-failed">
            <XCircle className="h-10 w-10 text-destructive" />
            <h1 className="text-[15px] font-semibold">Purchase not completed</h1>
            <p className="text-[12.5px] text-muted-foreground">
              {detail || 'Something went wrong on the way back from Stripe.'}
            </p>
            <Button variant="outline" onClick={backToMarketplace} className="mt-2 w-full gap-2" data-testid="payment-back-marketplace">
              <ChevronLeft className="h-4 w-4" /> Back to marketplace
            </Button>
          </div>
        )}

        {status === 'timeout' && (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="payment-timeout">
            <Loader2 className="h-8 w-8 text-muted-foreground" />
            <h1 className="text-[15px] font-semibold">Still waiting on Stripe…</h1>
            <p className="text-[12.5px] text-muted-foreground">
              This is unusual. Your project will appear in the workspace as soon as the payment settles.
              If you were charged but nothing shows up in a few minutes, contact support with the session id
              in your URL.
            </p>
            <Button onClick={openWorkspace} className="mt-2 w-full gap-2" data-testid="payment-open-workspace-timeout">
              Open workspace
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
